import React, { useState } from 'react';
import { X, Wrench, AlertCircle, Lightbulb, Settings, Package, MapPin, Star, Database, Sparkles } from 'lucide-react';
import { formatDisplayValue } from '../../utils/displayUtils';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { toast } from 'react-toastify';

const AIDiagnosisModal = ({ isOpen, onClose, diagnosisData, loading, deviceInfo, issueDescription, jobId, userToken, onRatingSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  
  if (!isOpen) return null;

  const handleRatingClick = async (value) => {
    if (isSubmittingRating) return;
    
    setRating(value);
    setIsSubmittingRating(true);
    
    try {
      // Send rating to backend
      if (onRatingSubmit) {
        await onRatingSubmit({
          rating: value,
          source: diagnosisData?.source || 'unknown',
          errorCode: diagnosisData?.errorCode || null,
          jobId: jobId
        });
      } else if (jobId && userToken) {
        // Fallback: send directly to API
        await axios.post(
          `${API_BASE_URL}/api/jobs/${jobId}/diagnosis-rating`,
          {
            rating: value,
            source: diagnosisData?.source || 'unknown',
            errorCode: diagnosisData?.errorCode || null
          },
          { headers: { Authorization: `Bearer ${userToken}` } }
        );
        toast.success('Hvala na oceni!');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Greška pri slanju ocene');
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'visok':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'srednji':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'nizak':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'visok':
        return 'Visok';
      case 'srednji':
        return 'Srednji';
      case 'nizak':
        return 'Nizak';
      default:
        return priority;
    }
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'jednostavno':
        return 'bg-green-500';
      case 'srednje':
        return 'bg-yellow-500';
      case 'složeno':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getComplexityLabel = (complexity) => {
    switch (complexity) {
      case 'jednostavno':
        return 'Jednostavno';
      case 'srednje':
        return 'Srednje';
      case 'složeno':
        return 'Složeno';
      default:
        return complexity;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-white">Dijagnostika i Preporuke</h2>
                {diagnosisData?.source && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-700">
                    {diagnosisData.source === 'local_database' ? (
                      <>
                        <Database className="w-3 h-3 text-green-400" />
                        <span className="text-green-400">Lokalna baza</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 text-blue-400" />
                        <span className="text-blue-400">AI</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              {deviceInfo && deviceInfo !== '-' && (
                <p className="text-sm text-gray-400">{deviceInfo}</p>
              )}
              {issueDescription && formatDisplayValue(issueDescription) !== '-' && (
                <p className="text-xs text-gray-500 mt-1 italic">"{formatDisplayValue(issueDescription)}"</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
              <p className="text-gray-400">Analiziram problem...</p>
            </div>
          ) : diagnosisData && !diagnosisData.error && !diagnosisData.insufficientInfo ? (
            <>
              {/* Complexity Badge */}
              {diagnosisData.estimatedComplexity && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400">Procena složenosti:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${getComplexityColor(diagnosisData.estimatedComplexity)}`}>
                    {getComplexityLabel(diagnosisData.estimatedComplexity)}
                  </span>
                </div>
              )}

              {/* Tools and Parts */}
              {diagnosisData.toolsAndParts && diagnosisData.toolsAndParts.length > 0 && (
                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings className="w-5 h-5 text-blue-400" />
                    <h3 className="text-base font-semibold text-white">Alat i Delovi za Poneti</h3>
                  </div>
                  <div className="space-y-2">
                    {diagnosisData.toolsAndParts.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-800 rounded border border-gray-700"
                      >
                        <div className="flex items-center gap-3">
                          {item.category === 'alat' ? (
                            <Wrench className="w-4 h-4 text-blue-400" />
                          ) : (
                            <Package className="w-4 h-4 text-green-400" />
                          )}
                          <span className="text-sm text-white">{item.name}</span>
                          <span className="text-xs text-gray-400">({item.category})</span>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(item.priority)}`}>
                          {getPriorityLabel(item.priority)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Possible Causes */}
              {diagnosisData.possibleCauses && diagnosisData.possibleCauses.length > 0 && (
                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                    <h3 className="text-base font-semibold text-white">Mogući Uzroci Problema</h3>
                  </div>
                  <div className="space-y-3">
                    {diagnosisData.possibleCauses.map((cause, index) => (
                      <div key={index} className="bg-gray-800 rounded p-3 border border-gray-700">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <p className="text-sm text-white flex-1">{cause.cause}</p>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            cause.type === 'specifičan' 
                              ? 'bg-purple-600 text-white' 
                              : 'bg-gray-600 text-white'
                          }`}>
                            {cause.type === 'specifičan' ? 'Specifičan' : 'Opšti'}
                          </span>
                        </div>
                        {/* Probability Bar */}
                        <div className="w-full bg-gray-700 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${
                              cause.probability >= 50 
                                ? 'bg-red-500' 
                                : cause.probability >= 25 
                                ? 'bg-yellow-500' 
                                : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, cause.probability)}%` }}
                          />
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-gray-400">{cause.type === 'specifičan' ? 'Vezano za ovaj model' : 'Čest problem u praksi'}</span>
                          <span className="text-xs font-semibold text-white">{cause.probability}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Practical Recommendations */}
              {diagnosisData.practicalRecommendations && diagnosisData.practicalRecommendations.length > 0 && (
                <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-5 h-5 text-green-400" />
                    <h3 className="text-base font-semibold text-white">Praktične Preporuke za Servisera</h3>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {diagnosisData.practicalRecommendations.map((recommendation, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-3 bg-gray-800 rounded border border-gray-700"
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-0.5">
                          <span className="text-xs font-semibold text-green-400">{index + 1}</span>
                        </div>
                        <p className="text-sm text-gray-300 flex-1">{recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* General Tips */}
              {diagnosisData.generalTips && (
                <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-blue-300 mb-1">Opšti Saveti</h3>
                      <p className="text-sm text-blue-200">{diagnosisData.generalTips}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Rating System */}
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-base font-semibold text-white">Ocenite ovo rešenje</h3>
                </div>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleRatingClick(value)}
                      onMouseEnter={() => setHoveredRating(value)}
                      onMouseLeave={() => setHoveredRating(0)}
                      disabled={isSubmittingRating}
                      className={`transition-all ${
                        isSubmittingRating ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 cursor-pointer'
                      }`}
                    >
                      <Star
                        className={`w-8 h-8 ${
                          value <= (hoveredRating || rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-gray-600 text-gray-600'
                        }`}
                      />
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="ml-2 text-sm text-gray-400">
                      {rating === 5 ? 'Odlično!' : rating === 4 ? 'Dobro' : rating === 3 ? 'OK' : rating === 2 ? 'Slabo' : 'Veoma slabo'}
                    </span>
                  )}
                </div>
                {rating > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Hvala na oceni! Vaša povratna informacija nam pomaže da poboljšamo rešenja.
                  </p>
                )}
              </div>
            </>
          ) : diagnosisData && diagnosisData.insufficientInfo ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
              <p className="text-yellow-300 font-medium mb-2">Nedovoljno informacija</p>
              <p className="text-gray-400 text-sm">{diagnosisData.message || 'Za AI preporuke potrebno je da posao ima opis problema ili informacije o uređaju'}</p>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg border border-gray-700 text-left">
                <p className="text-sm text-gray-300 mb-2">Potrebne informacije:</p>
                <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
                  <li>Opis problema (issueDescription)</li>
                  <li>Marka uređaja (deviceBrand)</li>
                  <li>Model uređaja (deviceModel)</li>
                  <li>Tip uređaja (deviceType)</li>
                </ul>
              </div>
            </div>
          ) : diagnosisData && diagnosisData.error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
              <p className="text-red-300 font-medium mb-2">Greška pri dobijanju AI preporuka</p>
              <p className="text-gray-400 text-sm">{diagnosisData.message || 'Molimo pokušajte ponovo'}</p>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nema dostupnih preporuka</p>
              <p className="text-sm mt-2">Proverite da li posao ima opis problema ili informacije o uređaju</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
          >
            Zatvori
          </button>
          {!loading && diagnosisData && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" />
              Nastavi Putovanje
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIDiagnosisModal;

